/**
 * Admin Routes — Require admin/operator authentication
 * Club admin, sysadmin, VBB operations, content management
 */
import React, { Suspense } from 'react';
import { Route } from 'react-router-dom';

import AdminSettings from '../pages/AdminSettings';
import ClubAdminDashboard from '../pages/ClubAdminDashboard';
import ClubPulseSettings from '../pages/ClubPulseSettings';
import ClubCreatorDashboard from '../pages/ClubCreatorDashboard';
import CreateClub from '../pages/CreateClub';
import SysadminDashboard from '../pages/SysadminDashboard';
import VBBOperationsCenter from '../pages/VBBOperationsCenter';
import BlogAdminDashboard from '../pages/BlogAdminDashboard';
import FeedSourcesAdmin from '../pages/FeedSourcesAdmin';
import EventSourcesAdmin from '../pages/EventSourcesAdmin';
import NewsSourcesAdmin from '../pages/NewsSourcesAdmin';
import SubmissionAdminDashboard from '../pages/SubmissionAdminDashboard';
import ModerationPanel from '../pages/ModerationPanel';
import BlockedUsers from '../pages/BlockedUsers';
import BannerManagement from '../pages/BannerManagement';
import ExternalSourcesAdmin from '../pages/ExternalSourcesAdmin';
import AdSystemConfig from '../pages/AdSystemConfig';
import OperatorDashboard from '../pages/OperatorDashboard';
import OperatorLegalPage from '../pages/OperatorLegalPage';
import InvestorOnePager from '../pages/InvestorOnePager';
import ClubDashboard from '../pages/ClubDashboard';
import AdminSetup from '../pages/AdminSetup';
import AdminIndependents from '../pages/AdminIndependents';
import AdminEmail from '../pages/AdminEmail';
import AdminPartnerPages from '../pages/AdminPartnerPages';
import AdminSeeding from '../pages/AdminSeeding';

// Lazy
const PromoCodesAdmin = React.lazy(() => import('../pages/PromoCodesAdmin'));
const ServiceSubmissionsAdmin = React.lazy(() => import('../pages/Admin/ServiceSubmissionsAdmin'));
const NGOVerificationAdmin = React.lazy(() => import('../pages/Admin/NGOVerificationAdmin'));
const ContentSuggestionsAdmin = React.lazy(() => import('../pages/Admin/ContentSuggestionsAdmin'));
const AdminNgosPage = React.lazy(() => import('../pages/admin/AdminNgosPage'));
const PartnerPipelinePage = React.lazy(() => import('../pages/admin/PartnerPipelinePage'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

export function getAdminRoutes(Layout, ProtectedRoute, VBBProtectedRoute) {
  return [
    // Admin Setup (bootstrap-key protected, not auth-protected)
    <Route key="/admin/setup" path="/admin/setup" element={<Layout><AdminSetup /></Layout>} />,

    // Club Admin
    <Route key="/admin" path="/admin" element={<ProtectedRoute><Layout><AdminSettings /></Layout></ProtectedRoute>} />,
    <Route key="/club-admin" path="/club-admin" element={<ProtectedRoute><Layout><ClubAdminDashboard /></Layout></ProtectedRoute>} />,
    <Route key="/club-admin/:slug/pulse-settings" path="/club-admin/:slug/pulse-settings" element={<ProtectedRoute><Layout><ClubPulseSettings /></Layout></ProtectedRoute>} />,
    <Route key="/creator-dashboard" path="/creator-dashboard" element={<ProtectedRoute><Layout><ClubCreatorDashboard /></Layout></ProtectedRoute>} />,
    <Route key="/clubs/create" path="/clubs/create" element={<ProtectedRoute><Layout><CreateClub /></Layout></ProtectedRoute>} />,

    // Sysadmin
    <Route key="/sysadmin" path="/sysadmin" element={<ProtectedRoute><Layout><SysadminDashboard /></Layout></ProtectedRoute>} />,
    <Route key="/admin/seeding" path="/admin/seeding" element={<ProtectedRoute><Layout><AdminSeeding /></Layout></ProtectedRoute>} />,

    // VBB Operations
    <Route key="/vbb" path="/vbb" element={<VBBProtectedRoute><Layout><VBBOperationsCenter /></Layout></VBBProtectedRoute>} />,
    <Route key="/vbb/operations" path="/vbb/operations" element={<VBBProtectedRoute><Layout><VBBOperationsCenter /></Layout></VBBProtectedRoute>} />,
    <Route key="/ops" path="/ops" element={<VBBProtectedRoute><Layout><VBBOperationsCenter /></Layout></VBBProtectedRoute>} />,
    <Route key="/ops/*" path="/ops/*" element={<VBBProtectedRoute><Layout><VBBOperationsCenter /></Layout></VBBProtectedRoute>} />,
    <Route key="/investor-one-pager" path="/investor-one-pager" element={<VBBProtectedRoute><Layout><InvestorOnePager /></Layout></VBBProtectedRoute>} />,

    // Content management
    <Route key="/blog/admin" path="/blog/admin" element={<ProtectedRoute><BlogAdminDashboard /></ProtectedRoute>} />,
    <Route key="/admin/blog" path="/admin/blog" element={<ProtectedRoute><BlogAdminDashboard /></ProtectedRoute>} />,
    <Route key="/admin/feed-sources" path="/admin/feed-sources" element={<ProtectedRoute><FeedSourcesAdmin /></ProtectedRoute>} />,
    <Route key="/admin/event-sources" path="/admin/event-sources" element={<ProtectedRoute><EventSourcesAdmin /></ProtectedRoute>} />,
    <Route key="/admin/news-sources" path="/admin/news-sources" element={<ProtectedRoute><NewsSourcesAdmin /></ProtectedRoute>} />,
    <Route key="/club/:slug/admin" path="/club/:slug/admin" element={<ProtectedRoute><FeedSourcesAdmin /></ProtectedRoute>} />,
    <Route key="/club/:slug/admin/sources" path="/club/:slug/admin/sources" element={<ProtectedRoute><FeedSourcesAdmin /></ProtectedRoute>} />,
    <Route key="/club/:slug/admin/submissions" path="/club/:slug/admin/submissions" element={<ProtectedRoute><Layout><SubmissionAdminDashboard /></Layout></ProtectedRoute>} />,

    // Moderation & Settings
    <Route key="/moderation" path="/moderation" element={<ProtectedRoute><Layout><ModerationPanel /></Layout></ProtectedRoute>} />,
    <Route key="/admin/banners" path="/admin/banners" element={<ProtectedRoute><Layout><BannerManagement /></Layout></ProtectedRoute>} />,
    <Route key="/admin/sources" path="/admin/sources" element={<ProtectedRoute><Layout><ExternalSourcesAdmin /></Layout></ProtectedRoute>} />,
    <Route key="/admin/ads" path="/admin/ads" element={<ProtectedRoute><Layout><AdSystemConfig /></Layout></ProtectedRoute>} />,
    <Route key="/operator" path="/operator" element={<ProtectedRoute><OperatorDashboard /></ProtectedRoute>} />,
    <Route key="/operator/legal" path="/operator/legal" element={<ProtectedRoute><Layout><OperatorLegalPage /></Layout></ProtectedRoute>} />,
    <Route key="/admin/finances" path="/admin/finances" element={<ProtectedRoute><Layout><ClubDashboard /></Layout></ProtectedRoute>} />,

    // Lazy admin pages
    <Route key="/admin/promo-codes" path="/admin/promo-codes" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><PromoCodesAdmin /></Suspense></Layout></ProtectedRoute>} />,
    <Route key="/admin/service-submissions" path="/admin/service-submissions" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><ServiceSubmissionsAdmin /></Suspense></Layout></ProtectedRoute>} />,
    <Route key="/admin/ngo-verification" path="/admin/ngo-verification" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><NGOVerificationAdmin /></Suspense></Layout></ProtectedRoute>} />,
    <Route key="/admin/content-suggestions" path="/admin/content-suggestions" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><ContentSuggestionsAdmin /></Suspense></Layout></ProtectedRoute>} />,
    <Route key="/admin/independents" path="/admin/independents" element={<ProtectedRoute><Layout><AdminIndependents /></Layout></ProtectedRoute>} />,
    <Route key="/admin/messages" path="/admin/messages" element={<ProtectedRoute><Layout><AdminEmail /></Layout></ProtectedRoute>} />,
    <Route key="/admin/partner-pages" path="/admin/partner-pages" element={<ProtectedRoute><Layout><AdminPartnerPages /></Layout></ProtectedRoute>} />,
    <Route key="/admin/ngos" path="/admin/ngos" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><AdminNgosPage /></Suspense></Layout></ProtectedRoute>} />,
    <Route key="/admin/partner-pipeline" path="/admin/partner-pipeline" element={<VBBProtectedRoute><Layout><Suspense fallback={<PageLoader />}><PartnerPipelinePage /></Suspense></Layout></VBBProtectedRoute>} />,
  ];
}
